var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useState } from "react";
import moment from "moment";
import { AccordionSet, Button, Col, Row } from "@folio/stripes/components";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormattedMessage, useIntl } from "react-intl";
import { DISPLAY_DATE_FORMAT } from "../../../utils/dateUtils";
import css from "./reports.module.css";
import { createProgressReportSchema } from "../../../utils/validationUtils";
import { generateProgressReport } from "../../../api/reportsService";
import { transformFormToApiParams, } from "../../../types/reports/progressReport";
import { FormatEnum, ProgressReportParamsRegistrationAgencyEnum, } from "../../../types/Api";
import { AgencySection } from "./sections/AgencySection";
import { PeriodSection } from "./sections/PeriodSection";
import { PricingSection } from "./sections/PricingSection";
import { ContactSection } from "./sections/ContactSection";
import { GdprSection } from "./sections/GdprSection";
import { ExportSection } from "./sections/ExportSection";
import { useReportMutation } from "../../../hooks/useReportMutation";
import { ReportSubmitSection } from "./ReportSubmitSection";
var ACCORDION_SECTIONS = [
    "agency",
    "period",
    "pricing",
    "contact",
    "gdpr",
    "export",
];
var getDefaultValues = function () { return ({
    registrationAgency: ProgressReportParamsRegistrationAgencyEnum.ISBN,
    periodFrom: moment().startOf("year").format(DISPLAY_DATE_FORMAT),
    periodTo: moment().format(DISPLAY_DATE_FORMAT),
    chargeForIsbn: false,
    pricePerIsbn: "",
    priceFor10: "",
    priceFor100: "",
    priceFor1000: "",
    annualMembership: "",
    registrationFee: "",
    contactPerson: "",
    organization: "",
    department: "",
    street: "",
    city: "",
    country: "",
    telephone1: "",
    telephone2: "",
    telephone3: "",
    fax: "",
    email1: "",
    email2: "",
    email3: "",
    url: "",
    gdprConsent: true,
    format: FormatEnum.PDF,
}); };
export var ProgressReportForm = function () {
    var formatMessage = useIntl().formatMessage;
    var _a = useState(Object.fromEntries(ACCORDION_SECTIONS.map(function (section) { return [section, true]; }))), accordionStatus = _a[0], setAccordionStatus = _a[1];
    var _b = useForm({
        resolver: zodResolver(createProgressReportSchema(formatMessage)),
        defaultValues: getDefaultValues(),
    }), control = _b.control, handleSubmit = _b.handleSubmit, watch = _b.watch;
    var chargeForIsbn = watch("chargeForIsbn");
    var reportMutation = useReportMutation({
        reportType: "progress-report",
        generateReport: function (ky, data) {
            var apiParams = transformFormToApiParams(data);
            return generateProgressReport(ky, apiParams, data.format);
        },
        successMessageId: "ui-br-agency.outputs.progress-report.success.generate",
        errorMessageId: "ui-br-agency.outputs.progress-report.error.generate",
    });
    var allExpanded = ACCORDION_SECTIONS.every(function (section) { return accordionStatus[section]; });
    var toggleAccordion = function (section) {
        setAccordionStatus(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[section] = !prev[section], _a)));
        });
    };
    var toggleExpandCollapseAll = function () {
        setAccordionStatus(Object.fromEntries(ACCORDION_SECTIONS.map(function (section) { return [section, !allExpanded]; })));
    };
    return (React.createElement("form", { onSubmit: handleSubmit(function (data) { return reportMutation.mutate(data); }) },
        React.createElement("h2", null,
            React.createElement(FormattedMessage, { id: "ui-br-agency.outputs.report.progress-report" })),
        React.createElement("p", null,
            React.createElement(FormattedMessage, { id: "ui-br-agency.outputs.progress-report.description" })),
        React.createElement(Row, null,
            React.createElement(Col, { xs: 12, className: css.expandCollapseRow },
                React.createElement(Button, { onClick: toggleExpandCollapseAll, buttonStyle: "default" }, allExpanded
                    ? formatMessage({ id: "ui-br-agency.button.collapse" })
                    : formatMessage({ id: "ui-br-agency.button.expand" })))),
        React.createElement(AccordionSet, null,
            React.createElement(AgencySection, { control: control, open: accordionStatus.agency, onToggle: function () { return toggleAccordion("agency"); } }),
            React.createElement(PeriodSection, { control: control, open: accordionStatus.period, onToggle: function () { return toggleAccordion("period"); } }),
            React.createElement(PricingSection, { control: control, open: accordionStatus.pricing, onToggle: function () { return toggleAccordion("pricing"); }, chargeForIsbn: chargeForIsbn }),
            React.createElement(ContactSection, { control: control, open: accordionStatus.contact, onToggle: function () { return toggleAccordion("contact"); } }),
            React.createElement(GdprSection, { control: control, open: accordionStatus.gdpr, onToggle: function () { return toggleAccordion("gdpr"); } }),
            React.createElement(ExportSection, { control: control, open: accordionStatus.export, onToggle: function () { return toggleAccordion("export"); } })),
        React.createElement(ReportSubmitSection, { reportJob: reportMutation.reportJob, isLoading: reportMutation.isLoading, generateButtonId: "ui-br-agency.outputs.progress-report.button.generate", successMessage: reportMutation.successMessage, onSuccessDismiss: reportMutation.clearSuccessMessage, errorModalOpen: reportMutation.errorModalOpen, errorMessage: reportMutation.errorMessage, onErrorClose: reportMutation.closeErrorModal })));
};
